import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, GraduationCap, FileText, Users, CheckCircle, Award,
  Plane, Home, BookOpen, TrendingUp, ArrowRight, Star, Target,
  Calculator, ClipboardCheck, DollarSign
} from 'lucide-react';
import Footer from '../components/Footer';

const Overseas = () => {
  const navigate = useNavigate();

  const stats = [
    { value: '2,000+', label: 'Students Placed', icon: Users },
    { value: '150+', label: 'Universities', icon: GraduationCap },
    { value: '20+', label: 'Countries', icon: Globe },
    { value: '95%', label: 'Visa Success', icon: Award }
  ];

  const services = [
    {
      icon: Target,
      title: 'University Selection',
      description: 'Expert guidance to choose the right university and program based on your profile, budget, and career goals.'
    },
    {
      icon: FileText,
      title: 'Application Support',
      description: 'Complete assistance with applications, essays, recommendations, and all required documentation.'
    },
    {
      icon: Award,
      title: 'Scholarship Guidance',
      description: 'Identify and apply for scholarships, grants, and financial aid opportunities to fund your education.'
    },
    {
      icon: Plane,
      title: 'Visa Assistance',
      description: 'Step-by-step support through visa application, interview preparation, and documentation.'
    },
    {
      icon: Home,
      title: 'Accommodation Help',
      description: 'Find suitable housing options near your university with verified listings and support.'
    },
    {
      icon: BookOpen,
      title: 'Pre-Departure Briefing',
      description: 'Comprehensive orientation on travel, culture, academics, and settling into your new country.'
    }
  ];

  const destinations = [
    {
      name: 'United Kingdom',
      universities: '50+',
      students: '800+',
      image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=600&h=400&fit=crop',
      popular: ['University of Oxford', 'Imperial College', 'LSE']
    },
    {
      name: 'United States',
      universities: '40+',
      students: '600+',
      image: 'https://images.unsplash.com/photo-1569982175971-d92b01cf8694?w=600&h=400&fit=crop',
      popular: ['MIT', 'Stanford', 'Harvard']
    },
    {
      name: 'Canada',
      universities: '30+',
      students: '500+',
      image: 'https://images.unsplash.com/photo-1517935706615-2717063c2225?w=600&h=400&fit=crop',
      popular: ['University of Toronto', 'UBC', 'McGill']
    },
    {
      name: 'Australia',
      universities: '25+',
      students: '300+',
      image: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=600&h=400&fit=crop',
      popular: ['University of Melbourne', 'ANU', 'UNSW']
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Free Consultation',
      description: 'Discuss your goals, budget, and preferences with our expert counselors'
    },
    {
      step: '2',
      title: 'Profile Evaluation',
      description: 'Comprehensive assessment of your academic profile and career objectives'
    },
    {
      step: '3',
      title: 'University Selection',
      description: 'Shortlist universities and programs that match your profile'
    },
    {
      step: '4',
      title: 'Application Process',
      description: 'Complete support with applications, essays, and documentation'
    },
    {
      step: '5',
      title: 'Offer Acceptance',
      description: 'Review offers and choose the best option for your future'
    },
    {
      step: '6',
      title: 'Visa & Departure',
      description: 'Visa application support and pre-departure preparation'
    }
  ];

  const testimonials = [
    {
      name: 'Chioma Okafor',
      university: 'Imperial College London',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face',
      content: 'Educential made my dream of studying at Imperial College a reality. The support was incredible from start to finish.',
      rating: 5
    },
    {
      name: 'Tunde Adebayo',
      university: 'University of Toronto',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      content: 'The scholarship guidance helped me secure 70% funding. Could not have done it without their expert advice.',
      rating: 5
    },
    {
      name: 'Amina Hassan',
      university: 'Stanford University',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      content: 'From application to visa, they guided me every step. Now I am living my dream at Stanford.',
      rating: 5
    }
  ];

  const freeTools = [
    {
      icon: Target,
      title: 'UK Pathfinder',
      description: 'Get personalized program recommendations based on your qualifications, goals, and preferences. No signup required.',
      path: '/overseas/uk/pathfinder',
      badge: 'Most Popular'
    },
    {
      icon: DollarSign,
      title: 'Financial Analysis',
      description: 'Calculate total study costs including tuition, living expenses, visa fees, and more for accurate budgeting.',
      path: '/overseas/uk/financial-analysis',
      badge: 'Free Tool'
    },
    {
      icon: ClipboardCheck,
      title: 'Readiness Assessment',
      description: 'Check if you have all required documents and qualifications ready for your study abroad application.',
      path: '/overseas/uk/readiness-assessment',
      badge: 'Free Tool'
    }
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white pt-20 pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Your Journey to
                <span className="text-yellow-500"> Global Education</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Complete guidance for studying abroad. From university selection to visa approval, we support you at every step of your international education journey.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <button
                  onClick={() => document.getElementById('free-tools-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center justify-center px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  Start Your Journey
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-lg border-2 border-gray-200 transition-all duration-200"
                >
                  Book Consultation
                </button>
              </div>

              <div className="flex items-center space-x-8">
                <div className="flex items-center">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">4.9/5 rating</span>
                </div>
                <div className="text-sm text-gray-600">2,000+ students placed</div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl h-96">
                <img
                  src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&h=600&fit=crop"
                  alt="Students studying abroad"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <Award className="w-7 h-7 text-yellow-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">95%</div>
                    <div className="text-sm text-gray-600">Visa Success Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="h-10 w-10 text-yellow-500 mx-auto mb-3" />
                  <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Study Abroad Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              End-to-end support for your international education journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="group p-8 rounded-2xl border border-gray-200 hover:border-yellow-500 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-yellow-500 transition-colors duration-300">
                    <Icon className="h-7 w-7 text-yellow-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Popular Study Destinations
            </h2>
            <p className="text-xl text-gray-600">
              Partner universities in top countries around the world
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {destinations.map((destination, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={destination.image}
                    alt={destination.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>

                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{destination.name}</h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Universities</span>
                      <span className="font-semibold text-gray-900">{destination.universities}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Students Placed</span>
                      <span className="font-semibold text-gray-900">{destination.students}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Popular Universities:</p>
                    <div className="space-y-1">
                      {destination.popular.map((uni, idx) => (
                        <div key={idx} className="flex items-start text-xs text-gray-700">
                          <CheckCircle className="h-3 w-3 text-yellow-500 mr-1 mt-0.5 flex-shrink-0" />
                          <span>{uni}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Your Path to Success
            </h2>
            <p className="text-xl text-gray-600">
              Simple six-step process from consultation to departure
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 h-full">
                  <div className="w-14 h-14 bg-yellow-500 rounded-xl flex items-center justify-center mb-6">
                    <span className="text-2xl font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Student Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Hear from students who achieved their study abroad dreams
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 border border-gray-200">
                <div className="flex items-center mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">{testimonial.content}</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.university}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Tools */}
      <section id="free-tools-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold mb-4">
              Free Planning Tools
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Start Your Journey Today
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Use our free planning tools to explore your study abroad options. No signup required until you're ready to apply.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {freeTools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-gradient-to-b from-gray-50 to-white rounded-2xl p-8 border-2 border-gray-200 hover:border-yellow-500 hover:shadow-2xl transition-all duration-300"
                >
                  {tool.badge && (
                    <div className="absolute top-4 right-4 px-3 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full">
                      {tool.badge}
                    </div>
                  )}

                  <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-yellow-500 transition-colors duration-300">
                    <Icon className="h-8 w-8 text-yellow-600 group-hover:text-white transition-colors duration-300" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{tool.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{tool.description}</p>

                  <button
                    onClick={() => navigate(tool.path)}
                    className="w-full inline-flex items-center justify-center px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    Try It Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 text-lg">
              <CheckCircle className="inline h-5 w-5 text-green-500 mr-2" />
              No credit card required. Use all tools for free.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Study Abroad?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Book a free consultation with our expert counselors and start your journey today
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="inline-flex items-center justify-center px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Book Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={() => navigate('/services-overview')}
              className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-100 text-gray-900 font-semibold rounded-lg transition-all duration-200"
            >
              View All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Overseas;
